// frontend/packages/business-console/src/pages/Payments.jsx
//
// Payments dashboard (design handoff Page 13, "the money lands here").
// The steady-state money view for Stripe Connect: one balance, the
// next payout, every payment with its fee and net, payout history,
// accepted methods, disputes/refunds.
//
// Data sources (one canonical source per metric, per the handoff
// reconciliation contract):
//   - GET /v2/stripe-connect/account   - status mirror (pill) + fee bps.
//   - GET /v2/stripe-connect/overview  - Stripe-side truth: balance,
//     payouts, bank, schedule. businessadmin-only; this page degrades
//     gracefully for managers/employees (the CRM-backed panels still
//     render).
//   - GET /v2/crm/payments/dashboard   - Mongo-side truth (webhook-
//     mirrored CrmPayment rows): 30d collected/fees/net, refunds,
//     disputes, avg time-to-pay, recent payments.
//
// Onboarding does NOT live here (D7): the setup surface is the
// embedded-Stripe page at `stripe-connect` ("Payment settings"); this
// page routes there when the account isn't ready.
//
// Styling: consumes the shared console redesign kit
// (components/primitives) + theme.custom.tokens, so colors stay in sync
// with every other page. The blue->purple gradient is identical in both
// themes.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import {
  useTokens,
  StatusPill,
  Surface,
  GradientHeroCard,
  PageHeader,
} from '../components/primitives';
import {
  getOrCreateStripeConnectAccount,
  getStripeConnectPayoutOverview,
  getCrmPaymentsDashboard,
  createStripeConnectInstantPayout,
} from '@upryzze/services';
import {
  demoPaymentsAccount,
  demoPaymentsOverview,
  demoPaymentsDashboard,
} from './Pulse/demoData';

// ── Formatting helpers ────────────────────────────────────────────────

function fmtMoney(cents, { exact = false } = {}) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '$0';
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: exact ? 2 : 0,
    maximumFractionDigits: exact ? 2 : 0,
  });
}

function fmtArrival(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(d) - startOfDay(now)) / 86400000);
  const dateLabel = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  if (diffDays === 0) return `Today, ${dateLabel}`;
  if (diffDays === 1) return `Tomorrow, ${dateLabel}`;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function fmtShortDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

const METHOD_LABELS = {
  card: 'Card',
  ach: 'ACH bank',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  cash: 'Cash',
  check: 'Check',
  other: 'Other',
};

// Payment state -> shared StatusPill props. The visual treatment lives
// in StatusPill; this is just the domain mapping.
const PAYMENT_STATE_PILL = {
  succeeded: { kind: 'success', label: 'Paid' },
  pending: { kind: 'warning', label: 'Processing' },
  failed: { kind: 'error', label: 'Failed' },
  refunded: { kind: 'neutral', label: 'Refunded' },
  partially_refunded: { kind: 'warning', label: 'Partial refund' },
  disputed: { kind: 'error', label: 'Disputed' },
};

function PaymentStatePill({ state }) {
  const props = PAYMENT_STATE_PILL[state] || { kind: 'neutral', label: state };
  return <StatusPill size="sm" {...props} />;
}

// ── Page sections ─────────────────────────────────────────────────────

function BalanceHero({ overview, onPayOut }) {
  const canInstant = (overview?.instantAvailableCents || 0) > 0;
  return (
    <GradientHeroCard sx={{ flex: { md: 1.5 } }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>
        Available balance
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 30, sm: 38 },
          fontWeight: 900,
          letterSpacing: '-1.2px',
          mt: '4px',
          lineHeight: 1.1,
        }}
      >
        {fmtMoney(overview.availableCents, { exact: true })}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: '14px',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10.5, opacity: 0.8, fontWeight: 600 }}>
            On the way to bank
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 800, mt: '2px' }}>
            {fmtMoney(overview.inTransitCents, { exact: true })}
          </Typography>
        </Box>
        <Box
          sx={{
            width: '1px',
            height: 28,
            bgcolor: 'rgba(255,255,255,.25)',
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Box>
          <Typography sx={{ fontSize: 10.5, opacity: 0.8, fontWeight: 600 }}>
            Next payout
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 800, mt: '2px' }}>
            {overview.nextPayout
              ? fmtArrival(overview.nextPayout.arrivalDate) || 'Scheduled'
              : 'None scheduled'}
          </Typography>
        </Box>
        {canInstant ? (
          <Button
            onClick={onPayOut}
            startIcon={<BoltOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={{
              ml: 'auto',
              border: '1px solid rgba(255,255,255,.4)',
              bgcolor: 'rgba(255,255,255,.12)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 11.5,
              px: '13px',
              py: '7px',
              borderRadius: '9px',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,.2)' },
            }}
          >
            Pay out now
          </Button>
        ) : null}
      </Box>
    </GradientHeroCard>
  );
}

function StatPair({ left, right }) {
  const t = useTokens();
  return (
    <Surface
      sx={{
        flex: 1,
        p: '13px 15px',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.muted }}>
          {left.label}
        </Typography>
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 900,
            letterSpacing: '-0.5px',
            mt: '2px',
            color: left.color || t.text,
          }}
        >
          {left.value}
        </Typography>
      </Box>
      <Box sx={{ ml: 'auto', textAlign: 'right' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.muted }}>
          {right.label}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '5px',
            justifyContent: 'flex-end',
            mt: '2px',
          }}
        >
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 900,
              letterSpacing: '-0.5px',
              color: right.color || t.text,
            }}
          >
            {right.value}
          </Typography>
          {right.note ? (
            <Typography
              sx={{ fontSize: 10.5, fontWeight: 800, color: t.green }}
            >
              {right.note}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Surface>
  );
}

const TABLE_COLUMNS = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 84px 92px', sm: '1fr 92px 84px 92px 96px' },
  gap: 1,
  px: 2,
  alignItems: 'center',
};

function RecentPaymentsTable({ dashboard, basePath }) {
  const t = useTokens();
  const rows = dashboard?.recent || [];
  return (
    <Surface sx={{ flex: { md: 1.6 }, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: '13px 16px 11px',
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
          Recent payments
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: t.faint, fontWeight: 600 }}>
          each one links to its invoice
        </Typography>
        <Typography
          component={RouterLink}
          to={`${basePath}/invoices`}
          sx={{
            ml: 'auto',
            fontSize: 11,
            fontWeight: 800,
            color: t.blue,
            textDecoration: 'none',
          }}
        >
          View invoices
        </Typography>
      </Box>
      <Box
        sx={{
          ...TABLE_COLUMNS,
          py: 1,
          borderTop: `1px solid ${t.hairline}`,
          bgcolor: t.tableHead,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.4px',
          textTransform: 'uppercase',
          color: t.faint,
        }}
      >
        <Box>Customer</Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Method</Box>
        <Box sx={{ textAlign: 'right' }}>Amount</Box>
        <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
          Net
        </Box>
        <Box sx={{ textAlign: 'right' }}>Status</Box>
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ p: '24px 16px', borderTop: `1px solid ${t.hairline}` }}>
          <Typography sx={{ fontSize: 12.5, color: t.muted }}>
            No payments yet. When a customer pays an invoice, it lands here with
            its fee and net.
          </Typography>
        </Box>
      ) : (
        rows.map((row) => {
          const refunded =
            row.state === 'refunded' || row.state === 'partially_refunded';
          return (
            <Box
              key={row.id}
              component={row.invoiceId ? RouterLink : 'div'}
              to={
                row.invoiceId
                  ? `${basePath}/invoices/${row.invoiceId}`
                  : undefined
              }
              sx={{
                ...TABLE_COLUMNS,
                py: '11px',
                borderTop: `1px solid ${t.hairline}`,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { bgcolor: t.subtle },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 800,
                    color: refunded ? t.textSecondary : t.text,
                  }}
                >
                  {row.customerName}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: 10.5,
                    color: refunded ? t.faint : t.blue,
                    fontWeight: 600,
                    mt: '1px',
                  }}
                >
                  {row.invoiceNumber || 'No invoice'}
                  {refunded && row.paidAt
                    ? ` · refunded ${fmtShortDate(row.paidAt)}`
                    : ''}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 11,
                  color: t.textSecondary,
                  fontWeight: 600,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {METHOD_LABELS[row.method] || row.method}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'right',
                  fontSize: 13,
                  fontWeight: 800,
                  color: refunded ? t.textSecondary : t.text,
                }}
              >
                {fmtMoney(row.amountCents)}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'right',
                  fontSize: 12.5,
                  fontWeight: 700,
                  display: { xs: 'none', sm: 'block' },
                  color:
                    row.state === 'refunded'
                      ? t.red
                      : row.netCents != null
                        ? t.greenText
                        : t.faint,
                }}
              >
                {row.state === 'refunded'
                  ? `-${fmtMoney(row.refundedCents)}`
                  : row.netCents != null
                    ? fmtMoney(row.netCents)
                    : '···'}
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <PaymentStatePill state={row.state} />
              </Box>
            </Box>
          );
        })
      )}
    </Surface>
  );
}

function PayoutsCard({ overview }) {
  const t = useTokens();
  const scheduleLabel = (overview.payoutSchedule || '').toUpperCase();
  return (
    <Surface sx={{ p: '14px 16px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
          Payouts to your bank
        </Typography>
        {scheduleLabel ? (
          <Box sx={{ ml: 'auto' }}>
            <StatusPill kind="info" size="sm" label={scheduleLabel} />
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          mt: '11px',
          p: '9px 11px',
          bgcolor: t.subtleBlue,
          border: `1px solid ${t.cardBorder}`,
          borderRadius: '10px',
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            bgcolor: t.blueBg,
            display: 'grid',
            placeItems: 'center',
            color: t.blue,
            flexShrink: 0,
          }}
        >
          <AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: 12, fontWeight: 800, color: t.text }}
          >
            {overview.bank
              ? `${overview.bank.bankName || 'Bank'} ···· ${overview.bank.last4}`
              : 'No bank account on file'}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: t.muted, mt: '1px' }}>
            {overview.nextPayout
              ? `Next: ${fmtMoney(overview.nextPayout.amountCents)} · ${fmtArrival(overview.nextPayout.arrivalDate).toLowerCase()}`
              : 'No payout on the way'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ mt: '11px' }}>
        {(overview.recentPayouts || []).length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: t.muted, py: 1 }}>
            Payouts appear here once money starts moving.
          </Typography>
        ) : (
          overview.recentPayouts.map((p, idx) => (
            <Box
              key={p.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                borderBottom:
                  idx === overview.recentPayouts.length - 1
                    ? 'none'
                    : `1px solid ${t.hairline}`,
              }}
            >
              <Typography
                sx={{ fontSize: 11.5, color: t.textSecondary, flex: 1 }}
              >
                {fmtShortDate(p.createdAt || p.arrivalDate)}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: t.text }}>
                {fmtMoney(p.amountCents)}
              </Typography>
              <StatusPill
                size="sm"
                kind={p.status === 'paid' ? 'success' : 'warning'}
                label={p.status === 'paid' ? 'Paid' : 'Pending'}
              />
            </Box>
          ))
        )}
      </Box>
    </Surface>
  );
}

function MethodsCard() {
  const t = useTokens();
  const methods = ['Cards', 'Apple Pay', 'Google Pay', 'ACH bank · lower fee'];
  return (
    <Surface sx={{ p: '14px 16px' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
        How customers can pay
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px', mt: '11px' }}>
        {methods.map((label) => (
          <Box
            key={label}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 11.5,
              fontWeight: 700,
              color: t.textSecondary,
              bgcolor: t.subtle,
              border: `1px solid ${t.cardBorder}`,
              borderRadius: '8px',
              px: '11px',
              py: '7px',
            }}
          >
            {label === 'Cards' ? (
              <CreditCardOutlinedIcon sx={{ fontSize: 14, color: t.blue }} />
            ) : null}
            {label}
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 10.5, color: t.muted, mt: '11px' }}>
        Customers pick a method on the invoice&apos;s payment page. Stripe shows
        the right options per device automatically.
      </Typography>
    </Surface>
  );
}

function DisputeTiles({ dashboard }) {
  const t = useTokens();
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Surface radius="12px" sx={{ flex: 1, p: '11px 13px' }}>
        <Typography sx={{ fontSize: 10.5, color: t.muted, fontWeight: 700 }}>
          Open disputes
        </Typography>
        <Typography
          sx={{
            fontSize: 19,
            fontWeight: 900,
            mt: '2px',
            color: dashboard.openDisputes > 0 ? t.red : t.text,
          }}
        >
          {dashboard.openDisputes}
        </Typography>
      </Surface>
      <Surface radius="12px" sx={{ flex: 1, p: '11px 13px' }}>
        <Typography sx={{ fontSize: 10.5, color: t.muted, fontWeight: 700 }}>
          Refunds · 30d
        </Typography>
        <Typography
          sx={{ fontSize: 19, fontWeight: 900, mt: '2px', color: t.text }}
        >
          {dashboard.refunds.count}{' '}
          {dashboard.refunds.count > 0 ? (
            <Box
              component="span"
              sx={{ fontSize: 11, color: t.faint, fontWeight: 700 }}
            >
              {'·'} {fmtMoney(dashboard.refunds.amountCents)}
            </Box>
          ) : null}
        </Typography>
      </Surface>
    </Box>
  );
}

function SetupCallout({ basePath, status }) {
  const t = useTokens();
  const submitted = Boolean(status?.detailsSubmitted);
  return (
    <Surface sx={{ p: { xs: 3, sm: 4 }, mt: 2 }}>
      <StatusPill
        kind="ai"
        label={
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <CreditCardOutlinedIcon sx={{ fontSize: 13 }} />
            Set up payments
          </Box>
        }
      />
      <Typography
        sx={{
          fontSize: 25,
          fontWeight: 900,
          letterSpacing: '-0.7px',
          mt: 2,
          color: t.text,
        }}
      >
        Get paid right in the console
      </Typography>
      <Typography
        sx={{
          fontSize: 13.5,
          lineHeight: 1.6,
          color: t.muted,
          mt: 1,
          maxWidth: 520,
        }}
      >
        {submitted
          ? 'Your details are with Stripe for verification. Payouts unlock as soon as verification completes, usually within a business day.'
          : 'A few quick steps and your invoices can take cards, Apple Pay, and bank transfers. Money goes straight to your account: funds are held by Stripe and paid to you, UpRyzze never touches them.'}
      </Typography>
      <Button
        component={RouterLink}
        to={`${basePath}/stripe-connect`}
        sx={{
          mt: 2.5,
          bgcolor: '#2563eb',
          color: '#fff',
          fontWeight: 700,
          fontSize: 12.5,
          px: '15px',
          py: '9px',
          borderRadius: '9px',
          textTransform: 'none',
          '&:hover': { bgcolor: '#1e40af' },
        }}
      >
        {submitted ? 'Check verification status' : 'Start setup'}
      </Button>
      <Typography
        sx={{ fontSize: 11, color: t.faint, fontWeight: 600, mt: 2.5 }}
      >
        Securely powered by{' '}
        <Box component="b" sx={{ color: t.stripe, fontSize: 13 }}>
          Stripe
        </Box>
      </Typography>
    </Surface>
  );
}

// ── Instant payout dialog ─────────────────────────────────────────────

function newRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function InstantPayoutDialog({ open, onClose, overview, onDone }) {
  const maxCents = overview?.instantAvailableCents || 0;
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // One requestId per dialog open - retries of the same confirmation
  // reuse it (idempotent at Stripe), a fresh open mints a new one.
  const [requestId, setRequestId] = useState(newRequestId);

  useEffect(() => {
    if (open) {
      setAmount((maxCents / 100).toFixed(2));
      setError(null);
      setRequestId(newRequestId());
    }
  }, [open, maxCents]);

  const amountCents = Math.round(parseFloat(amount || '0') * 100);
  const valid =
    Number.isInteger(amountCents) && amountCents > 0 && amountCents <= maxCents;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await createStripeConnectInstantPayout({
        amountCents,
        requestId,
      });
      onDone(data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'The payout could not be created.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Instant payout</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
          Send money from your available balance to your bank in minutes. Stripe
          charges a fee for instant payouts (about 1%). Your standard{' '}
          {`${(overview?.payoutSchedule || 'daily').toLowerCase()}`} payout is
          free.
        </Typography>
        <TextField
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          size="small"
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          helperText={`Available for instant payout: ${fmtMoney(maxCents, { exact: true })}`}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={!valid || busy}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {busy
            ? 'Sending…'
            : `Pay out ${valid ? fmtMoney(amountCents, { exact: true }) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

export default function Payments() {
  const t = useTokens();
  const navigate = useNavigate();
  const { business } = useContext(BusinessContext);
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  // Demo flag: `?demo=1` or localStorage `pulse.demo` = "1" (same toggle the
  // Pulse / Analytics / CRM pages use). Fills all three loads from fixtures
  // so the dashboard renders without a live Stripe account.
  const [searchParams] = useSearchParams();
  const isDemo = useMemo(() => {
    if (searchParams.get('demo') === '1') return true;
    try {
      return localStorage.getItem('pulse.demo') === '1';
    } catch {
      return false;
    }
  }, [searchParams]);

  // Three independent loads. `account` + `overview` are businessadmin-
  // only; a 403 on them must not blank the CRM-backed panels.
  const [account, setAccount] = useState({
    loading: true,
    data: null,
    forbidden: false,
  });
  const [overview, setOverview] = useState({
    loading: true,
    data: null,
    forbidden: false,
    error: null,
  });
  const [dashboard, setDashboard] = useState({
    loading: true,
    data: null,
    error: null,
  });

  const [payoutOpen, setPayoutOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await getStripeConnectPayoutOverview();
      setOverview({ loading: false, data, forbidden: false, error: null });
    } catch (err) {
      const code = err?.response?.status;
      setOverview({
        loading: false,
        data: null,
        forbidden: code === 403,
        // 409 = account not created yet; the setup callout owns that case.
        error:
          code === 409 ? null : err?.response?.data?.message || err?.message,
      });
    }
  }, []);

  useEffect(() => {
    if (isDemo) {
      setAccount({
        loading: false,
        data: demoPaymentsAccount,
        forbidden: false,
      });
      setOverview({
        loading: false,
        data: demoPaymentsOverview,
        forbidden: false,
        error: null,
      });
      setDashboard({
        loading: false,
        data: demoPaymentsDashboard,
        error: null,
      });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getOrCreateStripeConnectAccount();
        if (!cancelled) setAccount({ loading: false, data, forbidden: false });
      } catch (err) {
        if (!cancelled) {
          setAccount({
            loading: false,
            data: null,
            forbidden: err?.response?.status === 403,
          });
        }
      }
    })();
    (async () => {
      try {
        const { data } = await getCrmPaymentsDashboard();
        if (!cancelled) setDashboard({ loading: false, data, error: null });
      } catch (err) {
        if (!cancelled) {
          setDashboard({
            loading: false,
            data: null,
            error: err?.response?.data?.message || err?.message,
          });
        }
      }
    })();
    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [loadOverview, isDemo]);

  const status = account.data;
  const verified = Boolean(status?.chargesEnabled && status?.payoutsEnabled);
  const needsSetup = Boolean(status && !verified);

  const statusMeta = useMemo(() => {
    if (account.forbidden || account.loading) return null;
    if (verified) {
      return (
        <StatusPill kind="success" dot label="Payouts enabled · Verified" />
      );
    }
    if (status?.detailsSubmitted) {
      return <StatusPill kind="warning" dot label="Verification pending" />;
    }
    return <StatusPill kind="neutral" dot label="Not set up yet" />;
  }, [account.forbidden, account.loading, verified, status]);

  const d = dashboard.data;
  const avgDays = d?.avgTimeToPayDays;
  const priorAvgDays = d?.priorAvgTimeToPayDays;

  if (account.loading && dashboard.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const headerMeta = (
    <>
      {statusMeta}
      <Typography sx={{ fontSize: 11, color: t.faint, fontWeight: 600 }}>
        Powered by{' '}
        <Box component="b" sx={{ color: t.stripe }}>
          Stripe
        </Box>
      </Typography>
    </>
  );

  const headerActions = !account.forbidden ? (
    <Button
      onClick={() => navigate(`${basePath}/stripe-connect`)}
      sx={{
        border: `1px solid ${t.grayBorder}`,
        bgcolor: t.card,
        color: t.textSecondary,
        fontWeight: 700,
        fontSize: 12.5,
        px: '13px',
        py: '7px',
        borderRadius: '9px',
        textTransform: 'none',
        '&:hover': { bgcolor: t.subtle },
      }}
    >
      Payout settings
    </Button>
  ) : null;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2.5, sm: 3 } }}>
      <PageHeader title="Payments" meta={headerMeta} actions={headerActions} />

      {/* Not onboarded: the setup callout owns the page (the panels
          would all be empty anyway). */}
      {needsSetup && !verified ? (
        <SetupCallout basePath={basePath} status={status} />
      ) : (
        <>
          {/* Balance hero row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
              mt: '14px',
            }}
          >
            {overview.data ? (
              <BalanceHero
                overview={overview.data}
                onPayOut={() => setPayoutOpen(true)}
              />
            ) : (
              <Surface
                sx={{
                  flex: { md: 1.5 },
                  p: '18px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 120,
                }}
              >
                {overview.loading ? (
                  <CircularProgress size={22} />
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: t.muted }}>
                    {overview.forbidden
                      ? 'Balance and payouts are visible to business admins.'
                      : overview.error ||
                        'Balance is unavailable right now. Refresh to retry.'}
                  </Typography>
                )}
              </Surface>
            )}
            <Box
              sx={{
                flex: { md: 1 },
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <StatPair
                left={{
                  label: 'Collected · 30d',
                  value: fmtMoney(d?.totals?.collectedCents || 0),
                }}
                right={{
                  label: 'Net deposited',
                  value: fmtMoney(d?.totals?.netToClientCents || 0),
                  color: t.greenText,
                }}
              />
              <StatPair
                left={{
                  label: 'Fees · 30d',
                  value: fmtMoney(
                    (d?.totals?.stripeFeeCents || 0) +
                      (d?.totals?.applicationFeeCents || 0)
                  ),
                  color: t.textSecondary,
                }}
                right={{
                  label: 'Avg time to pay',
                  value: avgDays != null ? `${avgDays}d` : '···',
                  note:
                    avgDays != null &&
                    priorAvgDays != null &&
                    priorAvgDays > avgDays
                      ? `was ${priorAvgDays}d`
                      : null,
                }}
              />
            </Box>
          </Box>

          {/* Two-column body */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
              mt: 1.5,
              alignItems: 'stretch',
            }}
          >
            {dashboard.error ? (
              <Surface sx={{ flex: { md: 1.6 }, p: 3 }}>
                <Alert severity="error">{dashboard.error}</Alert>
              </Surface>
            ) : (
              <RecentPaymentsTable dashboard={d} basePath={basePath} />
            )}
            <Box
              sx={{
                flex: { md: 1 },
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
              }}
            >
              {overview.data ? <PayoutsCard overview={overview.data} /> : null}
              <MethodsCard />
              {d ? <DisputeTiles dashboard={d} /> : null}
            </Box>
          </Box>
        </>
      )}

      <InstantPayoutDialog
        open={payoutOpen}
        onClose={() => setPayoutOpen(false)}
        overview={overview.data}
        onDone={(payout) => {
          setPayoutOpen(false);
          setToast(
            `Instant payout of ${fmtMoney(payout.amountCents, { exact: true })} is on its way.`
          );
          loadOverview();
        }}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
}
